import { useEffect } from 'react';
import { lessonPath, navigate } from '../../app/router';
import { LESSONS } from '../../content/lessons';
import type { Lesson, Phase, Section } from '../../content/types';
import type { LessonStatus } from '../../state/progress';
import { parseMarkup } from '../Markup';
import { Blocks } from './Blocks';
import { GatePanel } from './GatePanel';

const PHASE_LABEL: Record<Phase, string> = {
  learn: 'Learn',
  predict: 'Predict',
  experiment: 'Experiment',
  inspect: 'Inspect',
  explain: 'Explain',
  answer: 'Answer',
};

const STEP_PHASES: Phase[] = ['predict', 'experiment', 'inspect', 'explain'];

/** Descriptive subtitle under the step caption, if any. */
function stepSubtitle(s: Section): string | null {
  if (s.phase === 'learn') return null;
  const label = PHASE_LABEL[s.phase];
  const t = s.title.trim();
  if (!t || t.toLowerCase() === label.toLowerCase()) return null;
  const stripped = t
    .replace(new RegExp(`^${label}\\s*:\\s*`, 'i'), '')
    .replace(new RegExp(`^${label}\\s+`, 'i'), '')
    .trim();
  if (!stripped || stripped.toLowerCase() === label.toLowerCase()) return null;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export function LessonView({ lesson, statuses }: { lesson: Lesson; statuses: LessonStatus[] }) {
  const prev = LESSONS[lesson.number - 1];
  const next = LESSONS[lesson.number + 1];
  const nextStatus = next ? statuses[next.number] : null;

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    requestAnimationFrame(() => {
      if (hash) {
        document.getElementById(hash)?.scrollIntoView({ block: 'start' });
        return;
      }
      // Lesson content scrolls inside `.pane`, not the window.
      const title = document.getElementById(`${lesson.id}-title`);
      const pane = title?.closest('.pane');
      if (pane instanceof HTMLElement) pane.scrollTop = 0;
      else window.scrollTo(0, 0);
    });
  }, [lesson.id]);

  const phases = STEP_PHASES.map((p) => ({ p, section: lesson.sections.find((s) => s.phase === p) }));

  return (
    <article className="lesson" aria-labelledby={`${lesson.id}-title`}>
      <header className="lesson-head">
        <p className="eyebrow">Module {lesson.number}</p>
        <h1 id={`${lesson.id}-title`} tabIndex={-1}>
          {lesson.title}
        </h1>
        <p className="lead">{lesson.summary}</p>
        <details className="objectives" open>
          <summary>By the end you will be able to</summary>
          <ul>
            {lesson.objectives.map((o, i) => (
              <li key={i}>{parseMarkup(o, `obj${i}`)}</li>
            ))}
          </ul>
        </details>
        <nav aria-labelledby={`${lesson.id}-steps`} className="phase-nav">
          <p id={`${lesson.id}-steps`} className="phase-nav-label">
            This module consists of the following steps:
          </p>
          <ol>
            {phases
              .filter(({ section }) => section)
              .map(({ p, section }) => (
                <li key={p}>
                  <a href={`#${section!.id}`}>{PHASE_LABEL[p]}</a>
                </li>
              ))}
            <li>
              <a href={`#${lesson.id}-gate`}>{PHASE_LABEL.answer}</a>
            </li>
          </ol>
        </nav>
      </header>

      {lesson.sections.map((s) => {
        const subtitle = stepSubtitle(s);
        return (
          <section key={s.id} id={s.id} className={`lesson-section phase-${s.phase}`} tabIndex={-1} aria-labelledby={`${s.id}-h`}>
            <h2 id={`${s.id}-h`}>
              {s.phase === 'learn' ? (
                s.title
              ) : (
                <>
                  <span className="phase-tag">{PHASE_LABEL[s.phase]}</span>
                  {subtitle ? <span className="phase-subtitle">{subtitle}</span> : null}
                </>
              )}
            </h2>
            <Blocks blocks={s.blocks} lesson={lesson} />
          </section>
        );
      })}

      <GatePanel key={lesson.id} lesson={lesson} />

      <nav className="lesson-foot" aria-label="Module navigation">
        {prev ? (
          <a className="btn ghost" href={lessonPath(prev.id)} onClick={(e) => (e.preventDefault(), navigate(lessonPath(prev.id)))}>
            ← Module {prev.number}
          </a>
        ) : (
          <span />
        )}
        {next &&
          (nextStatus?.unlocked ? (
            <a className="btn" href={lessonPath(next.id)} onClick={(e) => (e.preventDefault(), navigate(lessonPath(next.id)))}>
              Module {next.number}: {next.title} →
            </a>
          ) : (
            <span className="small muted" role="note">
              Module {next.number} unlocks when you pass this module’s check.
            </span>
          ))}
      </nav>
    </article>
  );
}
